import { inject, Injectable } from '@angular/core';
import { Coverage } from '../../interfaces/coverage.model';
import { PolicyData } from '../../interfaces/policy-data.model';
import { PolicySearch, PolicySearchSummary } from '../../interfaces/policy-summary.model';
import { PaymentFrequency } from '../../shared/enums/payment-frequency.enum';
import { ReferenceDataService } from '../../shared/services/reference-data.service';
import { calculateAge } from '../../shared/utils/get-age';
import { PolicyDataService } from '../services/policy-data.service';
import { PolicySummaryService } from '../services/policy-summary.service';

@Injectable({
  providedIn: 'root',
})
export class PolicyMediator {
  private readonly policySummaryCache = new Map<string, { data: PolicySearchSummary; expiry: number }>();
  private readonly policyDataCache = new Map<string, { data: PolicyData; expiry: number }>();
  private readonly policyDataService: PolicyDataService = inject(PolicyDataService);
  private readonly policySummaryService: PolicySummaryService = inject(PolicySummaryService);
  private readonly referenceDataService: ReferenceDataService = inject(ReferenceDataService);

  /**
   * Retrieves the policy summary for a given policy number, using local caching to reduce API calls.
   * If cached data exists and is not expired, returns it immediately. Otherwise, fetches fresh data
   * from the API and updates the cache.
   *
   * @param policyNumber - The policy number to get the summary for.
   * @param cacheDuration - (Optional) How long to keep the cache alive for this summary in milliseconds. Defaults to 30 minutes.
   * @returns A promise that resolves to the policy summary.
   */
  getPolicySummary(policyNumber: string, cacheDuration: number = 30 * 60 * 1000): Promise<PolicySearchSummary> {
    const currentTime = Date.now();
    const cached = this.policySummaryCache.get(policyNumber);

    if (cached && currentTime < cached.expiry) {
      return Promise.resolve(cached.data);
    }

    const search: PolicySearch = {
      policyNumber: policyNumber,
    };

    const result = this.policySummaryService
      .getPolicySummary(search)
      .then((response: PolicySearchSummary) => {
        this.policySummaryCache.set(policyNumber, {
          data: response,
          expiry: currentTime + cacheDuration,
        });
        return Promise.resolve(response);
      })
      .catch(error => {
        throw error;
      });
    return result;
  }

  /**
   * Retrieves policy data for a given policy number, using local caching to reduce API calls.
   * If cached data exists and is not expired, returns it immediately. Otherwise, fetches fresh data,
   * applies normalisation and calculation helpers, updates the cache, and persists the result in localStorage.
   *
   * @param policyNumber - The policy number to retrieve data for.
   * @param cacheDuration - (Optional) The cache expiry duration in milliseconds. Defaults to 30 minutes.
   * @returns A promise that resolves to the policy data.
   */
  getPolicyData(policyNumber: string, cacheDuration: number = 30 * 60 * 1000): Promise<PolicyData> {
    const currentTime = Date.now();
    const cached = this.policyDataCache.get(policyNumber);

    if (cached && currentTime < cached.expiry) {
      return Promise.resolve(cached.data);
    }
    const result = this.policyDataService
      .getPolicyData(policyNumber)
      .then((response: PolicyData) => {
        this.policyDataCache.set(policyNumber, {
          data: response,
          expiry: currentTime + cacheDuration,
        });
        this.initializeCoverageProperties(response);
        this.adjustPolicyFee(response);
        this.adjustCoveragePremiums(response);
        this.calculateCustomerAges(response);
        this.sortCoverages(response);

        return Promise.resolve(response);
      })
      .catch(error => {
        throw error;
      });
    return result;
  }

  // Helper methods
  /**
   * Initialises original sum insured and premium properties for each coverage in the policy data.
   * Removes any existing 'policyData' from localStorage before updating coverages.
   * This helps track original coverage values and ensures data consistency.
   *
   * @param {PolicyData} data - The PolicyData object to update coverages for.
   */
  private initializeCoverageProperties(data: PolicyData): void {
    if (!data?.coverages || !Array.isArray(data.coverages)) {
      return; // Exit if coverages are not present
    }
    localStorage.removeItem('policyData');

    data.coverages.forEach((coverage: Coverage) => {
      // Initialize sumInsuredOriginal if not already present
      if (!('sumInsuredOriginal' in coverage)) {
        coverage.sumInsuredOriginal = coverage.sumInsured?.amount ?? 0;
      }

      // Initialize premiumOriginal if not already present
      if (!('premiumOriginal' in coverage)) {
        coverage.premiumOriginal = coverage.premium?.amount ?? 0;
      }
    });
  }

  /**
   * Adjusts the policy fee on the PolicyData object to a weekly equivalent, based on payment frequency.
   *
   * @param {PolicyData} data - The PolicyData object to update the policy fee for.
   * @returns {void}
   */
  private adjustPolicyFee(data: PolicyData): void {
    if (!data.policy) {
      return;
    }
    const ratio = this.getPaymentFrequencyRatio(data.policy?.paymentFrequency ?? undefined);
    if (data.policy.fee?.amount != null) {
      data.policy.feeWeekly = data.policy.fee.amount / ratio;
    }
  }

  /**
   * Adjusts the premium properties for each coverage in the policy data.
   * Sets original and current premiums, and calculates the weekly premium based on the policy's payment frequency.
   *
   * @param {PolicyData} data - The PolicyData object to update premiums for.
   * @returns {void}
   */
  private adjustCoveragePremiums(data: PolicyData): void {
    if (!data.coverages || !Array.isArray(data.coverages)) {
      return;
    }
    const ratio = this.getPaymentFrequencyRatio(data.policy?.paymentFrequency ?? undefined);
    data.coverages.forEach(coverage => {
      if (coverage.sumInsured?.amount) {
        coverage.sumInsuredOriginal = coverage.sumInsured.amount;
      }
      if (coverage.premium?.amount != null) {
        coverage.premiumOriginal = coverage.premium.amount;
        coverage.premiumCurrent = coverage.premium.amount;
        coverage.premiumWeekly = coverage.premium.amount / ratio;
      }
    });
  }

  /**
   * Calculates and updates the age for each customer in the policy data based on their date of birth.
   *
   * @param {PolicyData} data - The PolicyData object containing the customers whose ages are to be calculated.
   * @returns {void}
   */
  private calculateCustomerAges(data: PolicyData): void {
    if (!data.customers || !Array.isArray(data.customers)) {
      return;
    }
    for (const customer of data.customers) {
      customer.age = calculateAge(customer?.dateOfBirth);
    }
  }

  /**
   * Sorts the coverages array in the PolicyData object according to the configured coverage display order.
   * Uses reference data to determine the order of each coverage and updates their 'order' property accordingly.
   *
   * @param {PolicyData} data - The PolicyData object containing the coverages to be sorted.
   * @returns {void}
   */
  private sortCoverages(data: PolicyData): void {
    const orderSet = this.referenceDataService.getReferenceDataSet('coverageDisplayOrder');
    if (orderSet && Array.isArray(orderSet)) {
      const map = new Map<string, string>();
      orderSet.forEach(item => {
        map.set(item.name, item.code);
      });
      if (Array.isArray(data.coverages)) {
        data.coverages.forEach(cov => {
          if (cov.code && map.has(cov.code)) {
            const codeStr = map.get(cov.code);
            if (codeStr) {
              cov.order = parseInt(codeStr, 10);
            }
          }
        });
        data.coverages.sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
      }
    }
  }

  private getPaymentFrequencyRatio(paymentFrequency?: string): number {
    return PaymentFrequency[paymentFrequency as keyof typeof PaymentFrequency] ?? 1;
  }
}
